import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View, Keyboard, TextInput, Alert, } from 'react-native';
import * as RNLocalize from 'react-native-localize';
import validator from 'email-validator';
import emoji from 'node-emoji';
import emailSpellChecker from '@zootools/email-spell-checker';
import { registerUser, requestEmailVerification } from 'actions';
import { useReSelector } from 'utils/hooksApi';
import createStyleSheets from 'utils/createStyleSheets';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { AppView, rem } from 'design-system';
import { useEmmaEditTag } from 'features/settings/personal-details/hooks/useEmmaEditTag';
import normalize from 'common/Normalize';
import useStyles from 'hooks/useStyles';
import Text from 'design-system/Text';
import { validateName } from 'utils/validation';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import StatusBar from 'design-system/StatusBar';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
const strings = {
    lastName: {
        title: 'Legal last name',
        input: 'Last name',
    },
    middleName: {
        title: 'Legal middle name',
        input: 'Middle name',
    },
    firstName: {
        title: 'Legal first name',
        input: 'First name',
    },
    email: {
        title: 'Email Address',
        input: 'Email',
    },
    urlHandle: {
        title: 'Emma Tag',
        input: 'Emma Tag',
    },
    PotName: {
        title: 'Name',
        input: 'name',
    },
};
export const ChangeDetailScreen = ({ navigation, route, }) => {
    const key = route.params?.key;
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    useLayoutEffect(() => {
        const headerTitle = strings[key].title;
        navigation.setOptions({
            title: headerTitle,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, colors.text.white, key]);
    /**
     * Overrides the default value, which is otherwise extracted from user record in cache using `key` navigation param
     */
    const defaultValueOverride = route.params?.defaultValue;
    /**
     * Overrides default "onSubmit" action, which otherwise depends on passed `key` navigation param
     * TODO v6 remove function as nav params
     */
    const onSubmitActionOverride = route.params?.onSuccess;
    const defaultValue = useReSelector((store) => defaultValueOverride ?? store.user.user[key]);
    const { isFetching: isFetchingTag, handleSetTag } = useEmmaEditTag(defaultValue);
    const { width } = useAppFrameDimensions();
    let fontSize = normalize(25);
    if (defaultValue && defaultValue.length >= 10 && defaultValue.length < 15) {
        fontSize = normalize(20);
    }
    else if (defaultValue && defaultValue.length >= 15) {
        fontSize = normalize(18);
    }
    const [state, setState] = useState({
        value: defaultValue,
        isEmailValid: true,
        fontSize,
    });
    const textInput = useRef(null);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const openNext = async () => {
        Keyboard.dismiss();
        const trimmedValue = state.value?.trim() || '';
        if (trimmedValue.length === 0)
            return;
        const body = {};
        switch (key) {
            case 'email': {
                if (!validator.validate(trimmedValue)) {
                    timeoutRef.current = setTimeout(() => {
                        Alert.alert(emoji.emojify('Not valid :face_with_rolling_eyes:'), 'This doesn’t seem to be a valid email address.', [{ text: 'OK', onPress: () => textInput.current?.focus() }]);
                    }, 50);
                    return;
                }
                const suggestedEmail = emailSpellChecker.run({
                    email: trimmedValue,
                });
                const submitEmail = async () => {
                    const result = await fetch(requestEmailVerification(trimmedValue));
                    if (result) {
                        navigation.navigateDeprecated('EmailVerification', {
                            flow: 'change',
                            newEmail: trimmedValue,
                        });
                    }
                };
                if (suggestedEmail) {
                    timeoutRef.current = setTimeout(() => {
                        Alert.alert(emoji.emojify('Are you sure? :face_with_rolling_eyes:'), `Did you mean ${suggestedEmail.full}?`, [
                            {
                                text: 'Continue anyway',
                                onPress: submitEmail,
                                style: 'cancel',
                            },
                            {
                                text: `Change to ${suggestedEmail.full}`,
                                onPress: () => {
                                    setState({ ...state, value: suggestedEmail.full });
                                },
                            },
                        ]);
                    }, 50);
                    return;
                }
                if (onSubmitActionOverride) {
                    onSubmitActionOverride(trimmedValue);
                    navigation.pop();
                    break;
                }
                await submitEmail();
                break;
            }
            case 'urlHandle': {
                if (onSubmitActionOverride) {
                    onSubmitActionOverride(trimmedValue);
                    navigation.pop();
                    break;
                }
                await handleSetTag(trimmedValue);
                break;
            }
            default: {
                if (key === 'firstName' || key === 'lastName') {
                    if (!validateName(trimmedValue)) {
                        timeoutRef.current = setTimeout(() => {
                            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter a valid name with at least 2 letters. Names can only contain letters A-Z (accents are fine), spaces, hyphens and apostrophes.', [
                                {
                                    text: 'OK',
                                    onPress: () => textInput && textInput.current?.focus(),
                                },
                            ]);
                        }, 50);
                        return;
                    }
                }
                if (onSubmitActionOverride) {
                    onSubmitActionOverride(trimmedValue);
                    navigation.pop();
                    break;
                }
                body[key] = trimmedValue;
                const timezone = RNLocalize.getTimeZone();
                const locales = RNLocalize.getLocales();
                const currencies = RNLocalize.getCurrencies();
                body.timezone = timezone;
                body.locale = locales[0].languageTag;
                [body.primary_currency] = currencies;
                const result = await fetch(registerUser(body));
                if (result)
                    navigation.goBack();
                else
                    Keyboard.dismiss();
                break;
            }
        }
    };
    const onChangeText = (data) => {
        let fontSize = normalize(25);
        if (data && data.length >= 10 && data.length < 15) {
            fontSize = normalize(20);
        }
        else if (data && data.length >= 15) {
            fontSize = normalize(18);
        }
        setState({ ...state, value: data, fontSize });
    };
    const fontStyle = useMemo(() => {
        if (state.value && state.value?.length >= 10 && state.value?.length < 15) {
            return { 'Text-20': true };
        }
        if (state.value && state.value?.length >= 15) {
            return { 'Text-18': true };
        }
        return { 'Text-26': true };
    }, [state.value]);
    const type = key === 'email' ? 'email-address' : 'default';
    const capitalise = key === 'email' ? 'none' : 'words';
    const autoCompleteType = key === 'email' ? 'email' : 'off';
    useAutoFocusInput(textInput);
    const renderTextInput = (style) => (<TextInput ref={textInput} keyboardType={type} placeholder={strings[key].input} autoCapitalize={capitalise} autoComplete={autoCompleteType} autoCorrect={false} allowFontScaling={false} value={state.value} onChangeText={onChangeText} selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder} underlineColorAndroid="transparent" style={[{ fontSize: state.fontSize }, style, styles.textinput]} returnKeyType="done" onSubmitEditing={openNext} testID="textInput"/>);
    const viewWidth = width - 40;
    return (<AppView type="primary" avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        <View style={styles.flex}>
          <View style={[styles.container, { alignItems: 'center' }]}>
            {key === 'urlHandle' ? (<View style={[styles.prefixContainer, { width: viewWidth }]}>
                <Text {...fontStyle} style={styles.prefix} color={colors.text.tertiary}>
                  @
                </Text>
                {renderTextInput()}
              </View>) : (renderTextInput({ width: viewWidth, textAlign: 'center' }))}
          </View>
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand isFetching={isFetching || isFetchingTag} onPress={openNext} title={key === 'email' ? 'Next' : 'Save'} testID="nextButton" withFrame={false}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
        marginHorizontal: rem(20),
        justifyContent: 'center',
    },
    flex: {
        flex: 1,
    },
    textinput: {
        fontFamily: 'Montserrat-Light',
        color: colors.text.primary,
    },
    prefixContainer: {
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    prefix: {
        color: colors.text.tertiary,
    },
}));
